/**
 * DownloadAppGate — Full-page "download the mobile app" screen shown to
 * non-admin team members who try to access the web app.
 *
 * Team members are intentionally kept on mobile. The ONLY web exception is the
 * "Important Info" post+email deep link (handled separately by ImportantPostView).
 * Everything else routes here.
 */
import React from 'react';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DownloadAppContent from './DownloadAppContent';
import { useDesign } from '@/contexts/DesignContext';
import { StudioAuthShell } from '@/components/studio/auth/StudioAuthParts';

export default function DownloadAppGate({ email = null, onSignOut }) {
  const { isStudio } = useDesign();

  // ---- Studio (doc 119 §24, mock frames 20 and 27): the card on the ground
  // under the header, the ghost Sign out outside the card ----
  if (isStudio) {
    return (
      <StudioAuthShell>
        <div className="st-au-card st-au-c" data-testid="download-app-gate">
          <DownloadAppContent email={email} variant="page" />
        </div>
        {onSignOut && (
          <Button
            variant="ghost"
            onClick={onSignOut}
            data-testid="gate-sign-out-btn"
            className="st-au-btn st-au-ghost"
          >
            <LogOut />
            Sign out
          </Button>
        )}
      </StudioAuthShell>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] flex flex-col items-center justify-center p-4"
      data-testid="download-app-gate"
    >
      <DownloadAppContent email={email} variant="page" />
      {onSignOut && (
        <Button
          variant="ghost"
          onClick={onSignOut}
          data-testid="gate-sign-out-btn"
          className="mt-10 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 gap-2"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </Button>
      )}
    </div>
  );
}
